import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useGetFeatureType } from '../../api-refactored/exam-configuration/useGetFeatureType';
import LoadingComponent from '../../components/LoadingComponent';
import { dropDownMappingToSelect, useAPIDropdown } from '../../hooks/dropdown';
import ExamRegistrationSearchAndFilter from '../../modules/examRegistration/components/nonNationalExam/ExamRegistrationSearchAndFilter';
import { EXAM_TYPE } from '../../routes/ExamRoute';
import { useGetNonNationalExamType } from '../../api-refactored/non-national-exam-registration/useGetNonNationalExamType';
import { useGetNonNationalApplicationStatusDropdown } from '../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationStatusDropdown';
import RouteKey from '../../routes/key';
import { useGetNonNationalReviewApplicationWithWithdrawal } from '../../api-refactored/non-national-exam-registration/useGetNonNationalReviewApplicationWithWithdrawal';
import { useGetNonNationalApplicationWithdrawalStatus } from '../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationWithdrawalStatus';

const { nonNationalExamApplication, nonNationalWithdrawalReason, withdrawal, summary } =
  RouteKey.admin.child;

const { admin } = RouteKey.child;

const useHeadersDropdown = (
  selectedExamType: string,
  setSelectedExamType: React.Dispatch<React.SetStateAction<string>>
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const examType = useGetNonNationalExamType({
    onSuccess: (data) => {
      if (data.length > 0) {
        setSelectedExamType(data[0].code);
      }
      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
  });
  const { functionCode: examTypeFunctionCode, typeMapping: examTypeMapping } = useAPIDropdown(
    () => examType,
    false,
    dropDownMappingToSelect
  );

  const featureType = useGetFeatureType({
    uuid: selectedExamType || '',
  });

  const { functionCode: featureTypeFunctionCode, typeMapping: featureTypeMapping } = useAPIDropdown(
    () => featureType,
    false,
    dropDownMappingToSelect
  );
  return {
    examTypeFunctionCode,
    examTypeMapping,
    featureTypeFunctionCode,
    featureTypeMapping,
    isLoading,
  };
};

const getReviewApplicationNavigation = (
  uuid: string,
  selectedExamTypeTitle: string,
  editable: boolean
) => {
  let path = '';
  let examType = '';
  let examTitle = '';
  const viewSummary = summary.route;
  switch (selectedExamTypeTitle) {
    case 'AEIS':
      path = editable
        ? `/admin/non-national-exam-application/${uuid}/aeis/parent-particulars`
        : `/admin/non-national-exam-application/${uuid}/aeis/${viewSummary}`;
      examType = EXAM_TYPE.AEIS;
      examTitle = 'Admissions Exercise for International Students (AEIS)';
      break;
    case 'S-AEIS':
      path = editable
        ? `/admin/non-national-exam-application/${uuid}/s-aeis/parent-particulars`
        : `/admin/non-national-exam-application/${uuid}/s-aeis/${viewSummary}`;
      examType = EXAM_TYPE.SAEIS;
      examTitle = 'Supplementary Admissions Exercise for International Students (S-AEIS)';
      break;
    case 'SPE':
      path = editable
        ? `/admin/non-national-exam-application/${uuid}/spe/candidate-details`
        : `/admin/non-national-exam-application/${uuid}/spe/${viewSummary}`;
      examType = EXAM_TYPE.SPERS;
      examTitle = 'Supplementary Placement Exercise (SPE)';
      break;
    case 'SPERS-Sec':
      path = editable
        ? `/admin/non-national-exam-application/${uuid}/spers-sec/candidate-details`
        : `/admin/non-national-exam-application/${uuid}/spers-sec/${viewSummary}`;
      examType = EXAM_TYPE.SPERSSec;
      examTitle = 'School Placement Exercise for Returning Singaporeans-Secondary (SPERS-Sec)';
      break;
    default:
      break;
  }

  return { path, examType, examTitle };
};

const AdminNonNationalExamApplication = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedExamType, setSelectedExamType] = useState<string>('');
  const [selectedExamTypeTitle, setSelectedExamTypeTitle] = useState<string>('');
  const [selectedUuid, setSelectedUuid] = useState<string>('');

  const { examTypeFunctionCode, isLoading } = useHeadersDropdown(
    selectedExamType,
    setSelectedExamType
  );

  const { data: allApplicationStatusDropdown } = useGetNonNationalApplicationStatusDropdown({
    allStatus: true,
  });

  const { status: apiGetApplicationStatus, remove: resetApiGetApplicationStatus } =
    useGetNonNationalReviewApplicationWithWithdrawal({
      uuid: selectedUuid,
      onSuccess: (res) => {
        const { examType, path, examTitle } = getReviewApplicationNavigation(
          selectedUuid,
          selectedExamTypeTitle,
          res.editable
        );
        setSelectedUuid('');
        resetApiGetApplicationStatus();
        history.push({ pathname: path, state: { examType, examTitle } });
      },
      enabled: !!selectedUuid,
    });

  const { data: applicationStatusDropdown } = useGetNonNationalApplicationWithdrawalStatus({});

  const isFinanceGroup = () =>
    applicationStatusDropdown?.some((d) => d.description === 'Withdrawn (Refunded)');
  const isStatusUpdatable = (status: string) => {
    if (status === 'Pending MOE Review (Withdrawal)') {
      return true;
    }
    return status === 'Refund Approved' && isFinanceGroup();
  };

  const handleNavigateByExamType = (uuid: string, status: string) => {
    if (
      status === 'Pending MOE Review (Withdrawal)' ||
      status === 'Refund Approved' ||
      status === 'Withdrawn (Refunded)' ||
      status === 'Withdrawn (No Refund)'
    ) {
      const targetRoute = isStatusUpdatable(status)
        ? nonNationalWithdrawalReason.route
        : summary.route;
      history.push(
        `/${admin}/${nonNationalExamApplication.route}/${uuid}/${withdrawal.route}/${targetRoute}`
      );
    } else {
      setSelectedUuid(uuid);
    }
  };

  useEffect(() => {
    if (selectedExamType) {
      const examTypeSelected = examTypeFunctionCode.find(
        (e: { key: unknown }) => e.key === selectedExamType
      );
      setSelectedExamTypeTitle(examTypeSelected.children);
    }
  }, [examTypeFunctionCode, selectedExamType]);

  return (
    <>
      {examTypeFunctionCode && allApplicationStatusDropdown && !isLoading ? (
        <ExamRegistrationSearchAndFilter
          isMobile={isMobile}
          examTypeFunctionCode={examTypeFunctionCode}
          selectedExamTypeTitle={selectedExamTypeTitle}
          onNavigate={handleNavigateByExamType}
          selectedExamType={selectedExamType || ''}
          setSelectedExamType={setSelectedExamType}
          allApplicationStatusDropdown={allApplicationStatusDropdown}
          apiGetApplicationStatus={apiGetApplicationStatus}
        />
      ) : (
        <LoadingComponent />
      )}
    </>
  );
};

export default AdminNonNationalExamApplication;
